<script setup>
import {
  ref,
  reactive,
  computed,
  onMounted,
} from 'vue';
import { values } from 'lodash';
import SeleccionSuscriptores from './SeleccionSuscriptores.vue';
import SuscriptoresSeleccionados from './SuscriptoresSeleccionados.vue';
import GeneracionSuscripciones from '../../../../../api/cifra/generacion_suscripciones';
import suscripciones from '../../../../../api/cifra/suscripciones';
import { onlyISODate } from '../../../../../libs/dates';
import Usuario from '../../../../../models/Usuario';
import { useGeneracionSuscripciones } from './GeneracionMixin';
import InfoServicio from './InfoServicio.vue';
import BotonGuardar from '../../../../../components/inputs/BotonGuardar.vue';

// Props
const props = defineProps({
  urlPlantillaAlumnos: {
    type: String,
    required: true,
  },
  copy: {
    type: Number,
    default: undefined,
  },
  transporteActivo: {
    type: Boolean,
    default: false,
  },
});

// Template refs
const btnTabSuscriptores = ref();

// Reactive data
const infoServicio = reactive({
  modalidad_id: null,
  fecha_alta: onlyISODate(new Date()),
  fecha_cobro: null,
  fecha_fin: null,
  fraccionar: false,
  numero_cuotas: 2,
  cuotas: [],
  etiqueta: '',
  observaciones_privadas: '',
});

const suscriptores = ref({});
const cargando = ref(true);
const infoServicioValido = ref(false);
const guardando = ref(false);

// Use composable for GeneracionMixin functionality
const {
  numeroSuscriptores,
  esTransporte,
  esTransporteEventuales,
} = useGeneracionSuscripciones(infoServicio, suscriptores);

// tipo es transporte_inscripciones o transporte_eventuales
const algunDatoTransporteSeleccionado = (tipo) => {
  const encontrado = values(suscriptores.value).filter((obj) => obj).find((suscriptor) => (
    suscriptor[tipo] && suscriptor[tipo].find((ins) => ins.seleccionada) === undefined
  ));
  return encontrado !== undefined;
};

const algunSuscriptorSinTurnoSeleccionado = computed(() => (
  algunDatoTransporteSeleccionado('transporte_inscripciones')
));

const algunSuscriptorSinEventualesSeleccionados = computed(() => (
  algunDatoTransporteSeleccionado('transporte_eventuales')
));

const btnTabSuscriptoresDisabled = computed(() => (
  !infoServicioValido.value ? 'disabled' : ''
));

const puedeGenerar = computed(() => {
  if (numeroSuscriptores.value < 1) {
    return false;
  }
  if (!esTransporte.value) {
    return true;
  }
  if (esTransporteEventuales.value) {
    return !algunSuscriptorSinEventualesSeleccionados.value;
  }
  return !algunSuscriptorSinTurnoSeleccionado.value;
});

// Methods
const guardar = () => {
  guardando.value = true;
  suscripciones.generar(infoServicio, values(suscriptores.value)).then(() => {
    guardando.value = false;
  }).catch(() => {
    guardando.value = false;
  });
};

const gotoBuscador = () => {
  // eslint-disable-next-line no-undef
  $(btnTabSuscriptores.value).click();
};

const modalidadesCargadas = () => {
  if (props.copy) {
    cargando.value = true;
    GeneracionSuscripciones.get(props.copy).then((result) => {
      const generacion = result.data;
      infoServicio.modalidad_id = generacion.modalidad_id;
      generacion.suscripciones.forEach((sus) => {
        suscriptores.value[sus.alumno.id] = new Usuario(sus.alumno);
      });
      cargando.value = false;
    });
  } else {
    cargando.value = false;
  }
};

// Lifecycle
onMounted(() => {
  infoServicio.transporteActivo = props.transporteActivo;
});

// Expose methods and properties to template
defineExpose({
  guardar,
  gotoBuscador,
  modalidadesCargadas,
});
</script>

<template>
  <div id="generacion-suscripciones-view">
    <ul class="nav nav-tabs" role="tablist">
      <li role="presentation" class="active">
        <a
          href="#tab-info-servicio"
          role="tab"
          data-toggle="tab"
        >
          {{ $I18n.t('informacion_del_servicio') }}
        </a>
      </li>
      <li role="presentation">
        <a
          ref="btnTabSuscriptores"
          :disabled="btnTabSuscriptoresDisabled"
          href="#tab-suscriptores"
          role="tab"
          data-toggle="tab"
        >{{ $I18n.t('suscriptores') }}</a>
      </li>
    </ul>
    <div class="tab-content">
      <div
        id="tab-info-servicio"
        role="tabpanel"
        class="tab-pane fade in active form-vertical clearfix"
      >
        <InfoServicio
          :info-servicio="infoServicio"
          :overlay="cargando"
          @siguiente="gotoBuscador"
          @form-valido="infoServicioValido = $event"
          @modalidades-cargadas="modalidadesCargadas"
        ></InfoServicio>
      </div>
      <div
        id="tab-suscriptores"
        role="tabpanel"
        class="tab-pane fade in form-vertical clearfix"
      >
        <SeleccionSuscriptores
          :info-servicio="infoServicio"
          :suscriptores="suscriptores"
          :url-plantilla-alumnos="urlPlantillaAlumnos"
          @tipotransporte-changed="infoServicio['tipoTransporte'] = $event"
        >
        </SeleccionSuscriptores>
        <transition enter-active-class="animated fadeInDown">
          <div v-show="numeroSuscriptores > 0" class="row">
            <div class="col-md-12">
              <SuscriptoresSeleccionados
                :info-servicio="infoServicio"
                :suscriptores="suscriptores"
              >
              </SuscriptoresSeleccionados>
              <div class="card bg-secondary m-t">
                <div class="row card-body">
                  <div class="col-sm-8">
                    <div v-if="numeroSuscriptores > 1">
                      <label class="control-label">{{ $I18n.t('etiqueta_seleccion') }}</label>
                      <input
                        v-model="infoServicio.etiqueta"
                        type="text"
                        class="form-control"
                      >
                    </div>
                  </div>
                  <div class="col-sm-4 m-t-label">
                    <BotonGuardar
                      :submitting="guardando"
                      :label="$I18n.t('Generar')"
                      class="pull-right"
                      :disabled="!puedeGenerar"
                      @click.prevent="guardar"
                    ></BotonGuardar>
                  </div>
                </div>
                <div
                  v-if="esTransporte &&
                    !esTransporteEventuales && algunSuscriptorSinTurnoSeleccionado"
                  class="text-info text-right p-md"
                >
                  <i class="fa fa-info-circle"></i>
                  {{ $I18n.t('debe_seleccionar_turnos_suscriptores') }}
                </div>
                <div
                  v-if="esTransporte &&
                    esTransporteEventuales && algunSuscriptorSinEventualesSeleccionados"
                  class="text-info text-right p-md"
                >
                  <i class="fa fa-info-circle"></i>
                  {{ $I18n.t('debe_seleccionar_eventuales_suscriptores') }}
                </div>
              </div>
            </div>
          </div>
        </transition>
      </div>
    </div>
  </div>
</template>
